<template>
  <div class="min-h-screen bg-white">
    <Header />

    <main class="min-h-screen">
      <!-- ═══ HERO ═══ -->
      <section class="relative overflow-hidden bg-white pt-32 pb-16 md:pt-40 md:pb-20">
        <div class="paw-pattern pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true" />
        <div class="relative mx-auto max-w-6xl px-4 text-center md:px-6">
          <h1 class="text-5xl font-bold leading-[1.05] tracking-tight text-foreground md:text-6xl">
            {{ $ts('hero.title') }}
          </h1>
          <p class="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            {{ $ts('hero.subtitle') }}
          </p>
        </div>
      </section>

      <!-- ═══ CONTENT ═══ -->
      <section class="bg-white py-12 md:py-16">
        <div class="mx-auto max-w-6xl px-4 md:px-6">
          <div class="grid gap-10 lg:grid-cols-2">

            <!-- Left: info + help articles -->
            <div class="flex flex-col gap-8">
              <!-- Contact info -->
              <div class="rounded-3xl border border-border/50 bg-white p-7">
                <h2 class="mb-5 text-lg font-bold text-foreground">{{ $ts('info.title') }}</h2>
                <div class="space-y-4">
                  <a
                    href="mailto:hello@bubba.pet"
                    class="flex items-center gap-3 text-sm font-medium text-foreground transition-opacity hover:opacity-70"
                  >
                    <Icon name="tabler:mail" size="28" class="shrink-0 text-foreground" />
                    hello@bubba.pet
                  </a>
                  <a
                    href="tel:+37060569477"
                    class="flex items-center gap-3 text-sm font-medium text-foreground transition-opacity hover:opacity-70"
                  >
                    <Icon name="tabler:phone" size="28" class="shrink-0 text-foreground" />
                    +370 605 69477
                  </a>
                </div>
              </div>

              <!-- Help articles -->
              <div class="rounded-3xl border border-border/50 bg-white p-7">
                <h2 class="mb-5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {{ $ts('helpArticles.title') }}
                </h2>
                <ul class="space-y-3">
                  <li v-for="article in helpArticles" :key="article.key">
                    <NuxtLink
                      :to="$localeRoute(article.href)"
                      class="flex items-center gap-3 rounded-2xl p-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
                    >
                      <Icon name="tabler:article" size="16" class="shrink-0 text-muted-foreground" />
                      {{ $ts(`helpArticles.${article.key}`) }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Right: contact form -->
            <div class="rounded-3xl border border-border/50 bg-white p-7">
              <h2 class="mb-6 text-lg font-bold text-foreground">{{ $ts('form.title') }}</h2>

              <form v-if="!submitted" class="space-y-4" @submit.prevent="handleSubmit">
                <div>
                  <label class="mb-1.5 block text-sm font-medium text-foreground">{{ $ts('form.name') }}</label>
                  <input
                    v-model="formData.name"
                    type="text"
                    :placeholder="$ts('form.namePlaceholder')"
                    required
                    class="h-11 w-full rounded-2xl border border-border/60 bg-muted/30 px-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                  />
                </div>
                <div>
                  <label class="mb-1.5 block text-sm font-medium text-foreground">{{ $ts('form.email') }}</label>
                  <input
                    v-model="formData.email"
                    type="email"
                    :placeholder="$ts('form.emailPlaceholder')"
                    required
                    class="h-11 w-full rounded-2xl border border-border/60 bg-muted/30 px-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                  />
                </div>
                <div>
                  <label class="mb-1.5 block text-sm font-medium text-foreground">{{ $ts('form.message') }}</label>
                  <textarea
                    v-model="formData.message"
                    :placeholder="$ts('form.messagePlaceholder')"
                    required
                    rows="5"
                    class="w-full resize-none rounded-2xl border border-border/60 bg-muted/30 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                  />
                </div>
                <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
                <button
                  type="submit"
                  :disabled="isSubmitting"
                  class="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-foreground px-8 text-sm font-semibold text-background transition-opacity hover:opacity-80 disabled:opacity-50"
                >
                  {{ isSubmitting ? $ts('form.sending') : $ts('form.submit') }}
                  <Icon v-if="!isSubmitting" name="tabler:arrow-right" size="16" />
                </button>
              </form>

              <div v-else class="flex flex-col items-center justify-center py-16 text-center">
                <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                  <Icon name="tabler:circle-check" size="32" class="text-emerald-500" />
                </div>
                <h3 class="mb-2 text-lg font-bold text-foreground">{{ $ts('form.successTitle') }}</h3>
                <p class="text-sm text-muted-foreground">{{ $ts('form.successMessage') }}</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import { useI18n, useSchemaOrg, defineWebPage, useSeoMeta } from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';
import { useConvexMutation } from '#composables/useConvex';
import { api } from '@convex/_generated/api';

definePageMeta({ layout: false });

const { $ts, $localeRoute } = useI18n();

useSeoMeta({
  title: () => $ts('seo.title'),
  description: () => $ts('seo.description'),
});

useSchemaOrg([defineWebPage({ '@type': 'ContactPage' })]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Contact' },
]);

const createCase = useConvexMutation(api.support.index.createCase);

const formData = reactive({ name: '', email: '', message: '' });
const isSubmitting = ref(false);
const submitted = ref(false);
const error = ref<string | null>(null);

const helpArticles = [
  { key: 'gettingStarted', href: '/help/getting-started' },
  { key: 'bookingGuide', href: '/help/booking-guide' },
  { key: 'accountSettings', href: '/help/account-settings' },
  { key: 'paymentsFaq', href: '/help/payments-faq' },
  { key: 'forBusinessPartners', href: '/help/for-business-partners' },
];

async function handleSubmit() {
  if (!formData.name || !formData.email || !formData.message) return;
  isSubmitting.value = true;
  error.value = null;
  try {
    await createCase({
      subject: `Contact form: ${formData.name}`,
      type: 'general_inquiry' as const,
      channel: 'web_form' as const,
      requesterApp: 'website' as const,
      requesterEmail: formData.email,
      requesterName: formData.name,
      initialMessage: formData.message,
    });
    submitted.value = true;
  } catch {
    error.value = $ts('form.errorMessage');
  } finally {
    isSubmitting.value = false;
  }
}
</script>
